
import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, Save, Download, Upload, ChevronDown, Plus, Trash2, AlertCircle, AlertTriangle, FileText, LayoutGrid, CheckCircle, RefreshCw, ArrowLeft, LogOut, Sun, Moon, Layers, Workflow, X } from 'lucide-react';
import { SimulationState, ProcessDefinition, AppNode } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

type PendingAction = 'save' | 'saveAndQuit' | 'export' | null;

// Minimal view-transition surface we actually use. Kept loose so it stays
// compatible regardless of whether the runtime lib.dom version knows about
// the View Transitions API.
interface ViewTransitionLike {
  ready: Promise<unknown>;
}

interface DocumentWithViewTransitions {
  startViewTransition?: (callback: () => void) => ViewTransitionLike;
}

interface TopBarProps {
  simulationState: SimulationState;
  onToggleSimulation: () => void;
  onReset: () => void;

  // File Operations
  onExport: (opts?: { bypassValidation?: boolean }) => void;
  onImport: (file: File) => void;
  
  // Host Callbacks
  onSave?: () => void;
  onSaveAndQuit?: () => void;
  onBack?: () => void;
  
  // Multi-process props
  processes: ProcessDefinition[];
  activeProcessId: string;
  onSelectProcess: (id: string) => void;
  onCreateProcess: () => void;
  onCreateSubProcess?: () => void;
  onDeleteProcess: (id: string) => void;
  currentNodes: AppNode[]; // To check for live errors
  
  // Auto Layout
  onAutoLayout: () => void;

  // Auto Save
  autoSaveState?: { isSaving: boolean; lastSaved: Date | null };

  // Validation
  validationErrors?: number;
  validationWarnings?: number;
  /** Error/warning counts per process id — drives the per-row icon in the
   *  process dropdown so issues in non-active processes stay visible. */
  processIssueCounts?: Record<string, { errors: number; warnings: number }>;
}

const TopBar: React.FC<TopBarProps> = ({ 
  simulationState, 
  onToggleSimulation, 
  onReset, 
  onExport, 
  onImport,
  onSave,
  onSaveAndQuit,
  onBack,
  processes,
  activeProcessId,
  onSelectProcess,
  onCreateProcess,
  onCreateSubProcess,
  onDeleteProcess,
  currentNodes,
  onAutoLayout,
  autoSaveState,
  validationErrors = 0,
  validationWarnings = 0,
  processIssueCounts = {},
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme, isControlled } = useTheme();

  const hasBlockingErrors = validationErrors > 0;

  const handleGuardedAction = (action: Exclude<PendingAction, null>, run: () => void) => {
      if (hasBlockingErrors) {
          setPendingAction(action);
      } else {
          run();
      }
  };

  const confirmPending = () => {
      const action = pendingAction;
      setPendingAction(null);
      if (action === 'save') onSave?.();
      else if (action === 'saveAndQuit') onSaveAndQuit?.();
      else if (action === 'export') onExport({ bypassValidation: true });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImport(file);
    }
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTheme = (e: React.MouseEvent) => {
    if (isControlled) return;

    const newTheme = theme === "light" ? "dark" : "light";
    const doc = document as unknown as DocumentWithViewTransitions;

    if (!doc.startViewTransition) {
        setTheme(newTheme);
        return;
    }

    const x = e.clientX;
    const y = e.clientY;
    const endRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
    );

    const transition = doc.startViewTransition(() => {
        setTheme(newTheme);
    });

    transition.ready.then(() => {
        const clipPath = [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
        ];
        
        document.documentElement.animate(
            {
                clipPath: clipPath,
            },
            {
                duration: 500,
                easing: "ease-in-out",
                // The new view is always growing on top
                pseudoElement: "::view-transition-new(root)",
            }
        );
    });
  };

  const hasErrors = (nodes: AppNode[] | undefined) => {
      if (!nodes || !Array.isArray(nodes)) return false;
      return nodes.some(n => n?.data?.status === 'error');
  };

  const activeProcess = processes.find(p => p.id === activeProcessId);
  const activeProcessName = activeProcess?.name || 'Unknown Process';
  const activeProcessHasErrors = hasErrors(currentNodes);
  const isActiveSubProcess = !!activeProcess?.isSubProcess;

  return (
    <header className="h-[64px] bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 shadow-sm z-50">
      
      {/* Branding & Process Selector */}
      <div className="flex items-center gap-4">
        {onBack && (
            <button 
                onClick={onBack}
                className="p-2 -ml-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg transition-colors"
                title="Go Back"
            >
                <ArrowLeft size={20} />
            </button>
        )}

        <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center text-white">
                <FileText size={18} />
            </div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white leading-tight tracking-tight hidden md:block">Peak Editor</h1>
        </div>

        <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-2"></div>

        {/* Process Dropdown */}
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all min-w-[200px] justify-between
                ${activeProcessHasErrors 
                    ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' 
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-teal-500 hover:bg-slate-50'}`}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {isActiveSubProcess ? <Layers size={14} className="text-slate-400 shrink-0"/> : <Workflow size={14} className="text-slate-400 shrink-0"/>}
                    <span className="text-sm font-semibold truncate max-w-[150px]">{activeProcessName}</span>
                    {isActiveSubProcess && (
                        <span className="text-[9px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">SUB</span>
                    )}
                    {activeProcessHasErrors && <AlertCircle size={14} className="text-red-500 shrink-0" />}
                </div>
                <ChevronDown size={14} className={`text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''} shrink-0`} />
            </button>

            {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl py-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        Process Definitions
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        {processes.map(proc => {
                            const isActive = proc.id === activeProcessId;
                            // Check errors: use live nodes if active, stored nodes if inactive
                            const procHasErrors = isActive ? activeProcessHasErrors : hasErrors(proc.nodes);
                            const isSub = !!proc.isSubProcess;
                            const counts = processIssueCounts[proc.id] || { errors: 0, warnings: 0 };
                            const showErrorBadge = counts.errors > 0;
                            const showWarningBadge = !showErrorBadge && counts.warnings > 0;

                            return (
                                <div
                                    key={proc.id}
                                    className={`group flex items-center justify-between px-3 py-2 mx-2 rounded-lg cursor-pointer transition-colors
                                    ${isActive ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                                    onClick={() => {
                                        onSelectProcess(proc.id);
                                        setIsDropdownOpen(false);
                                    }}
                                >
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        {procHasErrors ? (
                                            <AlertCircle size={14} className="text-red-500 shrink-0" />
                                        ) : (
                                            isSub ? (
                                                <Layers size={14} className={`${isActive ? 'text-teal-500' : 'text-slate-400'} shrink-0`} />
                                            ) : (
                                                <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${isActive ? 'border-teal-500' : 'border-slate-300'}`}>
                                                    {isActive && <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />}
                                                </div>
                                            )
                                        )}
                                        <span className={`text-sm truncate ${isActive ? 'text-teal-900 dark:text-teal-100 font-medium' : 'text-slate-600 dark:text-slate-400'}`}>
                                            {proc.name}
                                        </span>
                                        {isSub && (
                                            <span className="text-[9px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 shrink-0">SUB</span>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-1 shrink-0">
                                        {showErrorBadge && (
                                            <span
                                                className="flex items-center gap-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-1.5 py-0.5 rounded-full"
                                                title={`${counts.errors} error${counts.errors === 1 ? '' : 's'}${counts.warnings > 0 ? ` · ${counts.warnings} warning${counts.warnings === 1 ? '' : 's'}` : ''}`}
                                            >
                                                <AlertCircle size={10} />
                                                {counts.errors}
                                            </span>
                                        )}
                                        {showWarningBadge && (
                                            <span
                                                className="flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-1.5 py-0.5 rounded-full"
                                                title={`${counts.warnings} warning${counts.warnings === 1 ? '' : 's'}`}
                                            >
                                                <AlertTriangle size={10} />
                                                {counts.warnings}
                                            </span>
                                        )}
                                        {processes.length > 1 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteProcess(proc.id);
                                                }}
                                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                                                title="Delete Process"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="border-t border-slate-100 dark:border-slate-800 mt-2 pt-2 px-2 grid grid-cols-2 gap-2">
                        <button 
                            onClick={() => {
                                onCreateProcess();
                                setIsDropdownOpen(false);
                            }}
                            className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-slate-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/10 dark:text-slate-300 dark:hover:text-teal-400 rounded-lg transition-colors font-bold border border-slate-200 dark:border-slate-700"
                        >
                            <Plus size={12} />
                            Process
                        </button>
                        {onCreateSubProcess && (
                            <button 
                                onClick={() => {
                                    onCreateSubProcess();
                                    setIsDropdownOpen(false);
                                }}
                                className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-slate-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/10 dark:text-slate-300 dark:hover:text-teal-400 rounded-lg transition-colors font-bold border border-slate-200 dark:border-slate-700"
                            >
                                <Layers size={12} />
                                Sub-Process
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
      </div>

      {/* Simulation Controls */}
      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-800">
        <button
          onClick={onToggleSimulation}
          className={`
            flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all
            ${simulationState.isRunning 
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200' 
              : 'bg-white text-teal-700 hover:bg-teal-50 hover:text-teal-800 shadow-sm border border-slate-200'}
          `}
        >
          {simulationState.isRunning ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
          {simulationState.isRunning ? 'Pause' : 'Run Sim'}
        </button>

        <button 
          onClick={onReset}
          className="p-2 text-slate-500 hover:text-teal-600 hover:bg-white rounded-md transition-colors"
          title="Reset Simulation"
        >
          <RotateCcw size={16} />
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept=".bpjs,.json,.bpmn,.xml"
            onChange={handleFileChange} 
        />
        
        {/* Autosaved Indicator */}
        {autoSaveState && (
            <div className="hidden xl:flex items-center gap-2 mr-2">
                 {autoSaveState.isSaving ? (
                     <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono">
                         <RefreshCw size={12} className="animate-spin" />
                         SAVING...
                     </div>
                 ) : autoSaveState.lastSaved ? (
                     <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md font-medium" title={`Saved at ${autoSaveState.lastSaved.toLocaleTimeString()}`}>
                         <CheckCircle size={12} />
                         SAVED
                     </div>
                 ) : null}
            </div>
        )}

        {/* Theme Switcher - Hidden if Controlled */}
        {!isControlled && (
            <button
                onClick={toggleTheme}
                className="flex items-center justify-center p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:text-teal-400 dark:hover:bg-slate-900 rounded-lg transition-colors"
                title={`Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode`}
            >
                {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            </button>
        )}
        
        {/* Auto Layout Button */}
        <button 
            onClick={onAutoLayout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-teal-600 font-medium hover:bg-teal-50 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-teal-400 rounded-lg transition-colors"
            title="Auto Layout Nodes"
        >
            <LayoutGrid size={16} />
            <span className="hidden lg:inline">Layout</span>
        </button>

        <button onClick={handleImportClick} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-teal-600 font-medium hover:bg-teal-50 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-teal-400 rounded-lg transition-colors">
          <Upload size={16} />
          <span className="hidden lg:inline">Import</span>
        </button>
        {/* Compact validation chip — shown inline before the action buttons
            when blocking errors exist. Clicking an action opens a confirm
            modal rather than blocking silently. */}
        {hasBlockingErrors && (
            <div
                className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-[11px] font-medium text-red-700 dark:text-red-300"
                title={`${validationErrors} error${validationErrors === 1 ? '' : 's'}${validationWarnings > 0 ? ` · ${validationWarnings} warning${validationWarnings === 1 ? '' : 's'}` : ''} — confirmation required`}
            >
                <AlertCircle size={12} />
                <span>{validationErrors}{validationWarnings > 0 ? `+${validationWarnings}` : ''}</span>
            </div>
        )}

        <button
            onClick={() => handleGuardedAction('export', () => onExport())}
            title={hasBlockingErrors ? 'Export (validation confirmation required)' : 'Export'}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                hasBlockingErrors
                    ? 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30'
                    : 'text-slate-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-teal-400'
            }`}
        >
            <Download size={16} />
            <span className="hidden lg:inline">Export</span>
        </button>

        {onSave && (
            <button
                onClick={() => handleGuardedAction('save', () => onSave())}
                title={hasBlockingErrors ? 'Save (validation confirmation required)' : 'Save'}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm text-white ${
                    hasBlockingErrors
                        ? 'bg-red-600 hover:bg-red-700 ring-1 ring-red-300 dark:ring-red-800'
                        : 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/20'
                }`}
            >
              <Save size={16} />
              <span className="hidden lg:inline">Save</span>
            </button>
        )}

        {onSaveAndQuit && (
            <button
                onClick={() => handleGuardedAction('saveAndQuit', () => onSaveAndQuit())}
                title={hasBlockingErrors ? 'Save & Quit (validation confirmation required)' : 'Save & Quit'}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm text-white ${
                    hasBlockingErrors
                        ? 'bg-red-700 hover:bg-red-800 ring-1 ring-red-300 dark:ring-red-800'
                        : 'bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600'
                }`}
            >
              <LogOut size={16} />
              <span className="hidden lg:inline">Save & Quit</span>
            </button>
        )}
      </div>

      {pendingAction && (
          <ValidationConfirmModal
              action={pendingAction}
              errors={validationErrors}
              warnings={validationWarnings}
              onCancel={() => setPendingAction(null)}
              onConfirm={confirmPending}
          />
      )}
    </header>
  );
};

const ValidationConfirmModal: React.FC<{
    action: Exclude<PendingAction, null>;
    errors: number;
    warnings: number;
    onCancel: () => void;
    onConfirm: () => void;
}> = ({ action, errors, warnings, onCancel, onConfirm }) => {
    const actionLabel = action === 'save' ? 'Save' : action === 'saveAndQuit' ? 'Save & Quit' : 'Export';
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onCancel}>
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full mx-4 border border-slate-200 dark:border-slate-700" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between p-5 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-red-100 dark:bg-red-950/40 rounded-lg">
                            <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-slate-900 dark:text-white">Validation Issues Detected</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Proceeding will bypass validation</p>
                        </div>
                    </div>
                    <button onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded transition-colors">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg">
                        <AlertCircle size={16} className="text-red-500" />
                        <div className="text-sm">
                            <span className="font-semibold text-red-700 dark:text-red-300">{errors}</span>
                            <span className="text-slate-700 dark:text-slate-300"> blocking error{errors === 1 ? '' : 's'}</span>
                            {warnings > 0 && (
                                <>
                                    <span className="text-slate-400 mx-1">·</span>
                                    <span className="font-semibold text-amber-700 dark:text-amber-300">{warnings}</span>
                                    <span className="text-slate-700 dark:text-slate-300"> warning{warnings === 1 ? '' : 's'}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        The diagram has unresolved validation issues. Review them in the <strong>Issues</strong> tab before continuing.
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-500">
                        You can still proceed with <strong>{actionLabel}</strong>, but the exported data may not execute correctly downstream.
                    </p>
                </div>
                <div className="flex items-center justify-end gap-2 p-4 bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 rounded-b-xl">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                    >
                        Proceed Anyway
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TopBar;